import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtThousands } from "../api";
import { useLang } from "../i18n";
import { ArrowLeft, Shield, EyeOff } from "lucide-react";

export default function GuardianYearDetail() {
  const { year } = useParams();
  const { lang } = useLang();
  const it = lang === "it";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/guardian/trades/year/${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);

  const fmt = (v) => `${v >= 0 ? "+" : ""}${fmtThousands(v, lang)} €`;
  const summary = data?.summary;

  return (
    <div data-testid="guardian-year-detail-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <Link to="/guardian" data-testid="guardian-year-back-link"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Bund Guardian
      </Link>
      <p className="text-xs tracking-[0.3em] uppercase text-sky-400 font-mono mb-2 flex items-center gap-2">
        <Shield className="w-4 h-4" /> {it ? "Operazioni Guardian" : "Guardian operations"}
      </p>
      <div className="flex flex-wrap items-baseline gap-4">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-white">{year}</h1>
        {summary && (
          <>
            <span data-testid="guardian-year-pl" className={`font-mono text-2xl ${summary.pl_eur >= 0 ? "text-sky-400" : "text-red-400"}`}>
              {fmt(summary.pl_eur)} ({summary.pl_pct >= 0 ? "+" : ""}{summary.pl_pct}%)
            </span>
            <span className="font-mono text-sm text-slate-400">{summary.n_trades} ops · Win rate {summary.win_rate}%</span>
          </>
        )}
      </div>

      <div data-testid="guardian-year-ops" className="mt-10 border border-sky-500/20 bg-[#0B1220]">
        <div className="px-4 py-3 border-b border-sky-500/15">
          <h2 className="text-sm font-medium text-slate-200">
            {it ? `Tutte le operazioni ${year}` : `All ${year} operations`}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="text-left px-4 py-2">{it ? "Contratto" : "Contract"}</th>
                <th className="text-left px-2 py-2">{it ? "Direzione" : "Direction"}</th>
                <th className="text-right px-2 py-2">{it ? "Ingresso" : "Entry"}</th>
                <th className="text-right px-2 py-2">{it ? "Prezzo in" : "Entry px"}</th>
                <th className="text-right px-2 py-2">{it ? "Uscita" : "Exit"}</th>
                <th className="text-right px-2 py-2">{it ? "Prezzo out" : "Exit px"}</th>
                <th className="text-right px-2 py-2">{it ? "Esito" : "Outcome"}</th>
                <th className="text-right px-2 py-2">P/L €</th>
                <th className="text-right px-4 py-2">P/L %</th>
              </tr>
            </thead>
            <tbody>
              {(data?.operations || []).map((op, i) => (
                <tr key={i} className={`border-t border-white/5 ${op.masked ? "bg-sky-500/[0.04]" : ""}`}>
                  <td className="px-4 py-2 text-slate-300">{op.contract}</td>
                  <td className={`px-2 py-2 font-bold ${op.direction === "LONG" ? "text-sky-400" : op.direction === "SHORT" ? "text-red-400" : "text-slate-500"}`}>{op.direction}</td>
                  <td className="px-2 py-2 text-right text-slate-400">{op.open_date}</td>
                  <td className="px-2 py-2 text-right text-slate-300">{op.open_price != null ? op.open_price.toFixed(2) : "***"}</td>
                  <td className="px-2 py-2 text-right text-slate-400">{op.close_date || (op.status === "open" ? (it ? "in corso" : "ongoing") : "***")}</td>
                  <td className="px-2 py-2 text-right text-slate-300">{op.close_price != null ? op.close_price.toFixed(2) : "—"}</td>
                  <td className="px-2 py-2 text-right">
                    {op.masked ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-sky-400"><EyeOff className="w-3 h-3" />{it ? "RISERVATA" : "RESERVED"}</span>
                    ) : op.status === "open" ? (
                      <span className="text-[10px] text-amber-400">{it ? "APERTA" : "OPEN"}</span>
                    ) : (
                      <span className={`text-[10px] ${op.closed_by === "tp" ? "text-sky-400" : op.closed_by === "sl" ? "text-red-400" : "text-slate-400"}`}>
                        {op.closed_by === "tp" ? "TAKE PROFIT" : op.closed_by === "sl" ? "STOP LOSS" : "—"}
                      </span>
                    )}
                  </td>
                  <td className={`px-2 py-2 text-right ${(op.pl_eur ?? 0) >= 0 ? "text-sky-400" : "text-red-400"}`}>
                    {op.pl_eur != null ? `${op.pl_eur > 0 ? "+" : ""}${fmtThousands(op.pl_eur, lang)}` : "—"}
                  </td>
                  <td className={`px-4 py-2 text-right ${(op.pl_pct ?? 0) >= 0 ? "text-sky-400/80" : "text-red-400/80"}`}>
                    {op.pl_pct != null ? `${op.pl_pct > 0 ? "+" : ""}${op.pl_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data?.access === "public" && data?.operations?.some((o) => o.masked) && (
          <p className="px-4 py-3 border-t border-sky-500/15 text-[11px] text-slate-400 leading-relaxed">
            {it
              ? "Le operazioni contrassegnate come riservate sono visibili in chiaro solo agli abbonati Guardian; i dati tornano pubblici un giorno dopo la chiusura dell'operazione."
              : "Operations marked as reserved are only visible in clear to Guardian subscribers; data becomes public one day after the operation closes."}
          </p>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-4">
        {it
          ? "Percentuali calcolate sul capitale di riferimento Guardian di 10.000 €. Fill conservativi: a parità di giornata lo stop loss prevale sul take profit."
          : "Percentages calculated on the €10,000 Guardian reference capital. Conservative fills: on the same day the stop loss prevails over the take profit."}
      </p>
    </div>
  );
}
